import { Markdown } from '@/shared/ui/activity/markdown';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { LoadingStatus } from '@/shared/ui/spinner';

import { ArtifactFileList } from './artifact-file-list';

import type { SubmissionPolicy, WorkshopSolutionView } from '@mio/shared';

export interface SolutionQuery {
  data: WorkshopSolutionView | undefined;
  error: Error | null;
  isPending: boolean;
  isFetching: boolean;
  refetch: () => Promise<unknown>;
}

/** The authored reference, shown only once the policy says the learner may see it. */
export function WorkshopSolution({
  policy,
  solution,
}: {
  policy: SubmissionPolicy;
  solution: SolutionQuery;
}) {
  return (
    <section
      id="reference-solution"
      tabIndex={-1}
      aria-label="Reference solution"
      className="border-accent-line bg-accent-soft mt-6 rounded-lg border p-4 outline-offset-[-2px]"
    >
      <h3 className="text-fg text-lg font-semibold tracking-tight">Reference solution</h3>
      <p className="text-fg-muted mt-2 text-[13px] leading-6">
        {policy.lockReason === 'passed'
          ? 'You completed this workshop. Compare your approach with the reference and note where the two differ.'
          : `You have used all ${policy.limit} submissions for this workshop. Study how the reference meets each criterion, then continue to the next activity.`}
      </p>
      {!policy.solutionAvailable ? (
        <p className="text-fg-muted border-line bg-sidebar mt-4 rounded-lg border p-4 text-sm">
          A reference solution has not been published for this version of the workshop yet. Your
          review above still explains what is complete and what is missing.
        </p>
      ) : solution.isPending ? (
        <div className="mt-4">
          <LoadingStatus label="Loading the reference solution…" />
        </div>
      ) : solution.error ? (
        <div className="mt-4 space-y-3">
          <Alert>Could not load the reference solution. Check your connection and try again.</Alert>
          <Button
            variant="secondary"
            loading={solution.isFetching}
            loadingLabel="Loading reference…"
            onClick={() => void solution.refetch()}
          >
            Retry loading the reference
          </Button>
        </div>
      ) : solution.data ? (
        <div className="mt-4 space-y-4">
          {solution.data.source === 'current' ? (
            <p className="text-fg-muted text-xs">
              {solution.data.starterMatches === false
                ? 'This reference was written for the current version of this workshop. Your starter was published earlier, so file names or line numbers may differ slightly; compare the structure and the reasoning.'
                : 'This reference was published after you started; it matches the starter files you received.'}
            </p>
          ) : null}
          <div className="workshop-instructions text-sm">
            <Markdown source={solution.data.walkthrough} />
          </div>
          <h4 className="text-fg text-sm font-semibold">Reference files</h4>
          <ArtifactFileList
            files={solution.data.files}
            kind="Reference"
            emptyMessage="The reference solution has no files."
            defaultOpen
          />
        </div>
      ) : null}
    </section>
  );
}
